import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@1trading/react';
import { StatusChip } from '@/components/ui/status-chip';
import { EVENTS_COLOR, formatEventType, formatShare, getWeightBand, rankByContribution, WEIGHT_BAND_CHIP_VARIANTS } from '@/features/abuse/abuse-meta';
import type { AbuseSummary } from '@/services/api/abuse';

interface SummaryProps {
  summary: AbuseSummary | undefined;
  isLoading: boolean;
}

const ShareBar = ({ value, max }: { value: number; max: number }) => (
  <span aria-hidden className="h-2 rounded-full bg-muted">
    <span className="block h-2 rounded-full" style={{ width: `${max === 0 ? 0 : (value / max) * 100}%`, backgroundColor: EVENTS_COLOR }} />
  </span>
);
ShareBar.displayName = 'ShareBar';

const EmptyBreakdown = () => <p className="py-6 text-center text-sm text-muted-foreground">No events in this window</p>;
EmptyBreakdown.displayName = 'EmptyBreakdown';

const EventTypeBreakdown = ({ byType }: { byType: AbuseSummary['byType'] }) => {
  const rows = rankByContribution(byType);
  const max = Math.max(0, ...rows.map((row) => row.contribution));

  if (max === 0) return <EmptyBreakdown />;

  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li className="grid grid-cols-[minmax(0,12rem)_3.5rem_1fr_7rem] items-center gap-3 text-sm" key={row.eventType}>
          <span className="truncate" title={formatEventType(row.eventType)}>
            {formatEventType(row.eventType)}
          </span>
          <StatusChip className="justify-center px-2 py-0.5 text-xs tabular-nums" variant={WEIGHT_BAND_CHIP_VARIANTS[getWeightBand(row.weight)]}>
            w {row.weight}
          </StatusChip>
          <ShareBar max={max} value={row.contribution} />
          <span className="text-right tabular-nums">
            {row.contribution}
            <span className="ml-1.5 text-xs text-muted-foreground">{row.events} ev</span>
          </span>
        </li>
      ))}
    </ul>
  );
};
EventTypeBreakdown.displayName = 'EventTypeBreakdown';

const ServiceBreakdown = ({ byService }: { byService: AbuseSummary['byService'] }) => {
  const total = byService.reduce((sum, row) => sum + row.events, 0);
  const max = Math.max(0, ...byService.map((row) => row.events));

  if (total === 0) return <EmptyBreakdown />;

  return (
    <ul className="flex flex-col gap-3">
      {byService.map((row) => (
        <li className="flex flex-col gap-1 text-sm" key={row.service}>
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate">{row.service}</span>
            <span className="tabular-nums">
              {row.events}
              <span className="ml-1.5 text-xs text-muted-foreground">{formatShare(row.events, total)}</span>
            </span>
          </span>
          <ShareBar max={max} value={row.events} />
        </li>
      ))}
    </ul>
  );
};
ServiceBreakdown.displayName = 'ServiceBreakdown';

export const BreakdownCards = ({ summary, isLoading }: SummaryProps) => (
  <div className="grid gap-4 lg:grid-cols-3">
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle>By event type</CardTitle>
        <p className="text-xs text-muted-foreground">Ranked by score contributed: events × weight, before decay</p>
      </CardHeader>
      <CardContent>{isLoading || !summary ? <Skeleton className="h-48 w-full" /> : <EventTypeBreakdown byType={summary.byType} />}</CardContent>
    </Card>
    <Card>
      <CardHeader>
        <CardTitle>By service</CardTitle>
      </CardHeader>
      <CardContent>{isLoading || !summary ? <Skeleton className="h-24 w-full" /> : <ServiceBreakdown byService={summary.byService} />}</CardContent>
    </Card>
  </div>
);
BreakdownCards.displayName = 'BreakdownCards';
